import { KPI, Question, Resource, Response } from '@esg/esg-global-types';
import { Box, Grid, IconButton } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KPIQuestion from './KPIQuestion';
import sdg_logo from '../../../assets/sdg_logo.webp';
import gri_logo from '../../../assets/gri_logo.webp';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';
import { BodyText, Heading, MinorHeading } from '../../shared/typography/PrimaryTypography';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';
import SkeletonGrid, { SkeletonItem } from '../../shared/loading/SkeletonGrid';

interface KPIPageProps {
  loading: boolean;
  kpi: KPI;
  kpi_group_name: string;
  questions: Array<Question>;
  responses: Array<Response>;
  save_loading: boolean;
  show_gri_refs: boolean;
  show_sdgs: boolean;
  gri_resources: Array<Resource>;
  sdg_resources: Array<Resource>;
  handleUpdateResponses: (response: Response) => void;
  handleToggleGRIRefs: () => void;
  handleToggleSDGs: () => void;
}

/**
 * A wrapper component to list Questions relative to the given KPI.
 * @param {boolean} loading Flag for loading state for data from parent.
 * @param {KPI} kpi Current KPI of Questionnaire.
 * @param {string} kpi_group_name Name of KPI Group that KPI belongs to.
 * @param {Array<Question>} questions List of Questions relating to given KPI.
 * @param {Array<Response>} responses List of given Responses to Questions.
 * @param {boolean} save_loading The saving state of the Questionnaire to disable more inputs.
 * @param {Array<Resource>} gri_resources List of GRI related Resource Centre resources for questions search through for relevant compliance reference link.
 * @param {Array<Resource>} sdg_resources List of SDG related Resource Centre resources for questions search through for relevant sdg reference links.
 * @param {void} handleUpdateResponses Update Response list in parent on user input.
 * @returns {JSX.Element}
 */
const KPIPage = ({
  loading,
  kpi,
  questions,
  responses,
  save_loading,
  show_gri_refs,
  show_sdgs,
  gri_resources,
  sdg_resources,
  handleUpdateResponses,
  handleToggleGRIRefs,
  handleToggleSDGs
}: KPIPageProps) => {
  const sorted_questions: Array<Question> = questions.sort(
    (a: Question, b: Question) => a.order - b.order
  );

  const question_loading_grid: Array<SkeletonItem> = [
    {
      size: 0.5,
      variant: 'circular',
      width: 20,
      height: 20
    },
    {
      size: 9.8,
      variant: 'rounded',
      height: 30
    },
    {
      size: 0.8,
      variant: 'circular',
      width: 30,
      height: 30
    },
    {
      size: 0.8,
      variant: 'circular',
      width: 30,
      height: 30
    }
  ];
  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 8 }}>
        <Grid size={12} sx={{ textAlign: 'left' }}>
          {loading ? (
            <PrimarySkeleton variant="rounded" height={50} width={300} sx={{ my: 4 }} />
          ) : (
            <Heading weight="Bold" sx={{ my: 4 }}>{`${kpi.name}`}</Heading>
          )}
        </Grid>
        <Grid size={10} sx={{ textAlign: 'left', display: 'flex', gap: 4, alignItems: 'center' }}>
          <MinorHeading>Question</MinorHeading>
          <Box
            sx={{
              boxSizing: 'border-box',
              borderLeft: show_sdgs ? '2px solid #006A77' : 'none',
              borderRadius: 1,
              display: 'flex',
              gap: 2,
              px: 2,
              alignItems: 'center'
            }}
          >
            <img src={sdg_logo} alt="SDG" width={40} />
            <BodyText color="#006A77">{show_sdgs ? 'Hide SDGs' : 'Show SDGs'}</BodyText>
            <IconButton onClick={handleToggleSDGs}>
              {show_sdgs ? (
                <VisibilityOffOutlinedIcon sx={{ color: '#006A77' }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ color: '#006A77' }} />
              )}
            </IconButton>
          </Box>
          <Box
            sx={{
              boxSizing: 'border-box',
              borderLeft: show_gri_refs ? '2px solid #0E3A5A' : 'none',
              borderRadius: 1,
              display: 'flex',
              gap: 2,
              px: 2,
              alignItems: 'center'
            }}
          >
            <img src={gri_logo} alt="GRI" width={40} />
            <BodyText color="primary">{show_gri_refs ? 'Hide GRI' : 'Show GRI'}</BodyText>
            <IconButton onClick={handleToggleGRIRefs}>
              {show_gri_refs ? (
                <VisibilityOffOutlinedIcon color="primary" />
              ) : (
                <VisibilityOutlinedIcon color="primary" />
              )}
            </IconButton>
          </Box>
        </Grid>
        <Grid size={2} sx={{ display: 'flex', textAlign: 'center' }}>
          <MinorHeading sx={{ width: '40%' }}>Yes</MinorHeading>
          <MinorHeading sx={{ width: '40%' }}>No</MinorHeading>
        </Grid>
        <Grid size={12}>
          <PrimaryDivider sx={{ my: 2 }} />
        </Grid>
        {loading ? (
          <SkeletonGrid skeleton_items={question_loading_grid} />
        ) : (
          sorted_questions.map((question: Question) => {
            return (
              <Grid size={12} key={question.id}>
                <KPIQuestion
                  question={question}
                  response={responses.find(
                    (response: Response) => response.question.id === question.id
                  )}
                  save_loading={save_loading}
                  show_gri_refs={show_gri_refs}
                  show_sdgs={show_sdgs}
                  gri_resources={gri_resources}
                  sdg_resources={sdg_resources}
                  handleUpdateRespones={handleUpdateResponses}
                />
              </Grid>
            );
          })
        )}
      </Grid>
    </Box>
  );
};

export default KPIPage;
